import React from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
export function PackComparison() {
  const comparisonData = [{
    situation: 'Llegás a una situación clínica compleja',
    without: 'Dudás, improvisás o dependés del tutor',
    with: '30 casos resueltos paso a paso'
  }, {
    situation: 'Tenés que preparar un examen oral',
    without: 'Repasás teoría suelta',
    with: 'Casos organizados por área y complejidad'
  }, {
    situation: 'Necesitás practicar priorización',
    without: 'Te cuesta decidir qué hacer primero',
    with: 'Cada caso te entrena a pensar en orden'
  }, {
    situation: 'Querés mejorar tus registros',
    without: 'Lo hacés "como salga"',
    with: 'Casos con resolución y lógica clínica aplicada'
  }, {
    situation: 'Querés destacarte en prácticas',
    without: 'Hacés lo mismo que todos',
    with: 'Llegás con criterio clínico entrenado'
  }];
  const advancedContent = ['30 casos clínicos adicionales de complejidad alta', 'Casos de deterioro clínico progresivo', 'Casos de medicación y errores evitables', 'Casos respiratorios y postoperatorios', 'Casos de urgencias reales', 'Casos de priorización múltiple en sala', 'Casos de registros, comunicación y toma de decisiones', 'Entrenamiento de razonamiento clínico paso a paso'];
  return <section className="py-12 md:py-20 lg:py-24 bg-clinical-base" data-id="element-252">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-253">
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-12" data-id="element-254">
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-[10px] sm:text-xs font-semibold tracking-[0.22em] uppercase text-clinical-mid mb-4" data-id="element-255">
            Pack Clínica Avanzada
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl sm:text-4xl md:text-5xl font-black text-clinical-deep mb-4 leading-[1.05] uppercase" data-id="element-256">
            ¿Qué agrega el{' '}
            <span className="accent-italic normal-case" data-id="element-257">Pack Avanzado?</span>
          </motion.h2>
        </div>

        {/* Mobile: Cards */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="md:hidden space-y-3 mb-10" data-id="element-258">
          {comparisonData.map((row, idx) => <div key={idx} className="bg-white rounded-2xl border border-clinical-line overflow-hidden" data-id="element-259">
              <div className="bg-clinical-deep text-white px-4 py-3 text-sm font-bold" data-id="element-260">
                {row.situation}
              </div>
              <div className="divide-y divide-clinical-line" data-id="element-261">
                <div className="flex items-start gap-2.5 p-4" data-id="element-262">
                  <X className="w-4 h-4 text-clinical-ink/40 flex-shrink-0 mt-0.5" strokeWidth={2.5} data-id="element-263" />
                  <div data-id="element-264">
                    <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-clinical-ink/50 mb-0.5" data-id="element-265">
                      Sin Pack
                    </span>
                    <span className="text-sm text-clinical-ink/80" data-id="element-266">
                      {row.without}
                    </span>
                  </div>
                </div>
                <div className="flex items-start gap-2.5 p-4 bg-clinical-softer" data-id="element-267">
                  <Check className="w-4 h-4 text-clinical-mid flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-268" />
                  <div data-id="element-269">
                    <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-clinical-mid mb-0.5" data-id="element-270">
                      Con Pack
                    </span>
                    <span className="text-sm text-clinical-deep font-semibold" data-id="element-271">
                      {row.with}
                    </span>
                  </div>
                </div>
              </div>
            </div>)}
        </motion.div>

        {/* Desktop: Table */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="hidden md:block max-w-5xl mx-auto bg-white rounded-2xl overflow-hidden border border-clinical-line mb-12" data-id="element-272">
          <div className="overflow-x-auto" data-id="element-273">
            <table className="w-full text-left border-collapse min-w-[800px]" data-id="element-274">
              <thead data-id="element-275">
                <tr className="bg-clinical-deep text-white" data-id="element-276">
                  <th className="p-5 font-bold text-base w-2/5" data-id="element-277">
                    Situación en prácticas / internado
                  </th>
                  <th className="p-5 font-bold text-base w-1/4" data-id="element-278">
                    Sin Pack Avanzado
                  </th>
                  <th className="p-5 font-bold text-base w-1/3" data-id="element-279">
                    Con Pack Avanzado
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-clinical-line" data-id="element-280">
                {comparisonData.map((row, idx) => <tr key={idx} data-id="element-281">
                    <td className="p-5 text-clinical-deep font-semibold" data-id="element-282">
                      {row.situation}
                    </td>
                    <td className="p-5 text-clinical-ink/70" data-id="element-283">
                      <div className="flex items-start gap-2" data-id="element-284">
                        <X className="w-5 h-5 text-clinical-ink/40 flex-shrink-0 mt-0.5" strokeWidth={2} data-id="element-285" />
                        <span data-id="element-286">{row.without}</span>
                      </div>
                    </td>
                    <td className="p-5 text-clinical-deep font-medium bg-clinical-softer" data-id="element-287">
                      <div className="flex items-start gap-2" data-id="element-288">
                        <Check className="w-5 h-5 text-clinical-mid flex-shrink-0 mt-0.5" strokeWidth={2.5} data-id="element-289" />
                        <span data-id="element-290">{row.with}</span>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Advanced Content List */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-4xl mx-auto bg-white rounded-3xl p-6 md:p-10 border border-clinical-line" data-id="element-291">
          <h3 className="text-xl md:text-2xl font-bold text-clinical-deep mb-6 md:mb-7 text-center uppercase" data-id="element-292">
            El Pack <span className="accent-italic normal-case" data-id="element-293">incluye</span>
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3" data-id="element-294">
            {advancedContent.map((item, idx) => <div key={idx} className="flex items-start gap-3 p-1" data-id="element-295">
                <div className="bg-clinical-soft rounded-full p-1 mt-0.5 flex-shrink-0" data-id="element-296">
                  <Check className="w-3.5 h-3.5 text-clinical-mid" strokeWidth={3} data-id="element-297" />
                </div>
                <span className="text-clinical-ink text-sm md:text-base leading-snug" data-id="element-298">
                  {item}
                </span>
              </div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}